import {
  useState, useEffect, Dispatch, SetStateAction,
} from 'react';
import { trackSearchEvent, trackRecipeEvent } from '../../services/analytics';
import {
  Dialog, Box, InputBase, InputAdornment, Grid, Typography, Button,
  dialogClasses,
  IconButton,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useQuery } from '@tanstack/react-query';
import { secondary, straightFont } from '../../Constants';
import IRecipe from '../../interfaces/IRecipe';
import { getAllRecipes } from '../../services/DbService';
import RecipeMiniCard from '../RecipeCards/recipeMiniCard';
import { normalizeIngredient } from '../../utils/ingredientHelpers';
import { useTheme } from '../../providers/ThemeProvider';

type SortOption = 'latest' | 'popular';

interface ISearchBoxProps {
  open: boolean;
  setShow: Dispatch<SetStateAction<boolean>>;
}

export default function SearchBox(props: ISearchBoxProps) {
  const { open, setShow } = props;
  const { mode, colors } = useTheme();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<IRecipe[]>([]);
  const [resultsToShow, setResultsToShow] = useState<number>(12);
  const [sortBy, setSortBy] = useState<SortOption>('latest');

  const { data: allRecipes, error } = useQuery(
    ['recipes'], // Standardized key - shares cache with other pages
    getAllRecipes,
  );

  if (error) {
    return (
      <Box>
        <Typography color="error">Error loading recipes</Typography>
      </Box>
    );
  }

  const handleClose = () => {
    setShow(false);
    setSearchQuery('');
    setResultsToShow(12);
  };

  const handleRecipeClick = (recipe: IRecipe) => {
    trackRecipeEvent('click_from_search', recipe.id, recipe.title);
    handleClose();
  };

  const loadMoreResults = () => {
    trackSearchEvent('load_more', searchQuery || 'browse', resultsToShow + 12);
    setResultsToShow(resultsToShow + 12);
  };

  function searchRecipes(searchTerm: string): IRecipe[] {
    const term = searchTerm.toLowerCase();
    const matchedIds = new Set<string>();
    const recipes: IRecipe[] = [];

    // Helper to add recipe if not already added
    const addRecipe = (recipe: IRecipe) => {
      if (!matchedIds.has(recipe.id)) {
        matchedIds.add(recipe.id);
        recipes.push(recipe);
      }
    };

    // Search title (highest priority)
    allRecipes?.filter(
      (recipe: IRecipe) => recipe.title.toLowerCase().includes(term),
    ).forEach(addRecipe);

    // Search tags
    allRecipes?.filter(
      (recipe: IRecipe) => recipe.tags?.some((tag) => tag.toLowerCase().includes(term)),
    ).forEach(addRecipe);

    // Search keywords
    allRecipes?.filter(
      (recipe: IRecipe) => recipe.keywords?.some((kw) => kw.toLowerCase().includes(term)),
    ).forEach(addRecipe);

    // Search ingredients
    allRecipes?.filter((recipe) => recipe.ingredients.some(
      (ingredient) => normalizeIngredient(ingredient).name
        ?.toLowerCase().includes(term),
    )).forEach(addRecipe);

    // Search cuisine and category
    allRecipes?.filter(
      (recipe: IRecipe) => recipe.cuisine?.toLowerCase().includes(term)
        || recipe.category?.toLowerCase().includes(term),
    ).forEach(addRecipe);

    // Search description
    allRecipes?.filter(
      (recipe: IRecipe) => recipe.description?.toLowerCase().includes(term),
    ).forEach(addRecipe);

    return recipes;
  }

  useEffect(() => {
    if (open && searchQuery.trim()) {
      const fetchResults = () => {
        const results = searchRecipes(searchQuery);
        setSearchResults(results);
        trackSearchEvent('perform_search', searchQuery, results.length);
      };

      const timeoutId = setTimeout(fetchResults, 300);
      return () => clearTimeout(timeoutId);
    }
    if (!searchQuery.trim()) {
      setSearchResults([]);
    }
  }, [searchQuery, open]);

  // Get recipes to display - search results or all recipes sorted
  const getDisplayRecipes = (): IRecipe[] => {
    if (searchQuery.trim() && searchResults.length > 0) {
      return searchResults;
    }

    const recipes = [...(allRecipes ?? [])];
    if (sortBy === 'latest') {
      return recipes.sort((a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime());
    }
    return recipes.sort((a, b) => (b.favorites || 0) - (a.favorites || 0));
  };

  const displayRecipes = getDisplayRecipes();
  const hasSearchQuery = searchQuery.trim().length > 0;

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={handleClose}
      transitionDuration={{
        enter: 200,
        exit: 0,
      }}
      PaperProps={{
        sx: {
          zIndex: 9999,
          mt: 15,
          overflow: 'hidden',
          maxHeight: '80vh',
          borderRadius: 3,
          backgroundColor: colors.surface,
          border: '1px solid',
          borderColor: colors.border,
        },
      }}
      sx={{
        [`& .${dialogClasses.container}`]: {
          alignItems: 'flex-start',
        },
      }}
    >
      {/* Close Button */}
      <IconButton
        onClick={handleClose}
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          zIndex: 9999,
          color: colors.text,
          '&:hover': {
            backgroundColor: alpha(colors.text, 0.08),
          },
        }}
      >
        <CloseIcon />
      </IconButton>

      {/* Search Content */}
      <Box sx={{ p: 3, overflowY: 'auto', maxHeight: 'calc(80vh - 24px)' }}>
        {/* Search Input */}
        <InputBase
          fullWidth
          autoFocus
          placeholder="Search recipes..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          startAdornment={(
            <InputAdornment position="start">
              <SearchIcon sx={{ color: secondary }} />
            </InputAdornment>
          )}
          sx={{
            backgroundColor: colors.cardBackground,
            borderRadius: 2,
            border: '1px solid',
            borderColor: colors.border,
            padding: '12px 16px',
            fontFamily: straightFont,
            color: colors.text,
            boxShadow: mode === 'light' ? '0px 2px 6px rgba(0, 0, 0, 0.06)' : 'none',
            '& .MuiInputBase-input::placeholder': {
              color: colors.textSecondary,
              opacity: 1,
            },
            '&:hover': {
              borderColor: secondary,
            },
            '&.Mui-focused': {
              borderColor: secondary,
              boxShadow: `0px 0px 0px 2px ${alpha(secondary, 0.2)}`,
            },
          }}
        />

        {/* Header with count and sort */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 3,
            mb: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontFamily: straightFont,
              fontWeight: 600,
              color: colors.text,
              fontSize: '1.1rem',
            }}
          >
            {hasSearchQuery
              ? `${displayRecipes.length} result${displayRecipes.length !== 1 ? 's' : ''} for "${searchQuery}"`
              : `Browse ${allRecipes?.length ?? 0} Recipes`}
          </Typography>

          {!hasSearchQuery && (
            <FormControl size="small">
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                sx={{
                  fontFamily: straightFont,
                  fontSize: '0.875rem',
                  color: colors.text,
                  backgroundColor: colors.cardBackground,
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: colors.border,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: secondary,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: secondary,
                  },
                  '& .MuiSelect-icon': {
                    color: colors.textSecondary,
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: colors.surface,
                      borderRadius: 2,
                    },
                  },
                }}
              >
                <MenuItem value="latest" sx={{ fontFamily: straightFont, color: colors.text }}>
                  Latest
                </MenuItem>
                <MenuItem value="popular" sx={{ fontFamily: straightFont, color: colors.text }}>
                  Most Popular
                </MenuItem>
              </Select>
            </FormControl>
          )}
        </Box>

        {/* Recipe Grid */}
        {displayRecipes.length > 0 ? (
          <>
            <Grid container spacing={2}>
              {displayRecipes.slice(0, resultsToShow).map((recipe: IRecipe) => (
                <Grid item xs={6} sm={4} md={3} key={recipe.id}>
                  <RecipeMiniCard
                    recipe={recipe}
                    setShowSearch={() => handleRecipeClick(recipe)}
                  />
                </Grid>
              ))}
            </Grid>

            {/* Load More Button */}
            {resultsToShow < displayRecipes.length && (
              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={loadMoreResults}
                  sx={{
                    backgroundColor: secondary,
                    color: '#fff',
                    fontFamily: straightFont,
                    fontWeight: 600,
                    px: 4,
                    py: 1,
                    borderRadius: 2,
                    textTransform: 'none',
                    boxShadow: `0 4px 14px ${alpha(secondary, 0.35)}`,
                    '&:hover': {
                      backgroundColor: alpha(secondary, 0.9),
                      boxShadow: `0 6px 20px ${alpha(secondary, 0.45)}`,
                    },
                  }}
                >
                  Load More
                </Button>
              </Box>
            )}
          </>
        ) : hasSearchQuery ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography
              sx={{
                fontFamily: straightFont,
                color: colors.textSecondary,
                fontSize: '1rem',
              }}
            >
              No recipes found for &ldquo;{searchQuery}&rdquo;
            </Typography>
          </Box>
        ) : null}
      </Box>
    </Dialog>
  );
}
